/**
 * Shopify Chat API functions - session-based chat with Temporal + Redis SSE
 */

import { postRequestWithAuth, getRequestWithAuth, deleteRequestWithAuth, patchRequestWithAuth, getEventStream, getBlobRequestWithAuth } from "~/lib/api";

// Types
export interface Artifact {
  filename: string;
  title: string;
  artifact_type: "html" | "csv" | "xlsx" | "image";
}

export interface ToolCall {
  name: string;
  display_name: string;
  status: "running" | "completed";
  content_position: number;
}

export interface ShopifyChatSession {
  session_id: string;
  connection_id: string;
  title: string;
  is_busy: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShopifyChatMessage {
  _id: string;
  connection_id: string;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  artifacts: Artifact[];
  tools: ToolCall[];
  is_streaming: boolean;
  created_at: string;
  updated_at: string;
}

export interface SendMessageResponse {
  input_message_id: string;
  output_message_id: string;
}

export interface MessagesResponse {
  messages: ShopifyChatMessage[];
}

export interface SessionsResponse {
  sessions: ShopifyChatSession[];
}

// Session API functions

/**
 * Get all sessions for a connection.
 */
export async function getSessions(
  connectionId: string
): Promise<ShopifyChatSession[]> {
  const response: SessionsResponse = await getRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions`
  );
  return response.sessions;
}

/**
 * Create a new chat session.
 */
export async function createSession(
  connectionId: string,
  firstMessage: string = ""
): Promise<ShopifyChatSession> {
  return await postRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions`,
    { first_message: firstMessage }
  );
}

/**
 * Delete (soft) a session.
 */
export async function deleteSession(
  connectionId: string,
  sessionId: string
): Promise<void> {
  await deleteRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions/${sessionId}`
  );
}

/**
 * Rename a session.
 */
export async function renameSession(
  connectionId: string,
  sessionId: string,
  title: string
): Promise<void> {
  await patchRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions/${sessionId}`,
    { title }
  );
}

// Message API functions

/**
 * Send a message to the Shopify chat.
 * Returns message IDs - use connectToStream() to receive the response.
 */
export async function sendMessage(
  connectionId: string,
  sessionId: string,
  message: string
): Promise<SendMessageResponse> {
  return await postRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions/${sessionId}/sendMessage`,
    { message }
  );
}

/**
 * Get all messages for a session.
 */
export async function getMessages(
  connectionId: string,
  sessionId: string
): Promise<ShopifyChatMessage[]> {
  const response: MessagesResponse = await getRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/sessions/${sessionId}/messages`
  );
  return response.messages;
}

/**
 * Get a single message by ID (for refreshing after stream ends).
 */
export async function getMessage(
  connectionId: string,
  messageId: string
): Promise<ShopifyChatMessage> {
  return await getRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/message/${messageId}`
  );
}

/**
 * Connect to the SSE stream for a message.
 * Use this after calling sendMessage() to receive the streaming response.
 */
export function connectToStream(
  connectionId: string,
  sessionId: string,
  messageId: string
): EventSource {
  return getEventStream(
    `/api/v1/shopifyChat/${connectionId}/sessions/${sessionId}/stream?message_id=${messageId}`
  );
}

/**
 * Fetch an artifact file as a blob.
 * Returns a blob that can be converted to a blob URL for display.
 */
export async function getArtifactBlob(connectionId: string, filename: string): Promise<Blob> {
  const response = await getBlobRequestWithAuth(
    `/api/v1/shopifyChat/${connectionId}/artifact/${filename}`
  );
  return response.data;
}

// Event parsing helpers

export interface StreamEvent {
  type: "system" | "token" | "tool_start" | "tool_end";
  data: string;
}

// Map raw tool names to friendly display names
export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  query_data: "Querying data",
  run_analysis: "Analyzing data",
  save_artifact: "Creating visualization",
};

/**
 * Parse a raw SSE event message.
 * Returns an array of events from the message.
 */
export function parseStreamEvent(eventData: string): StreamEvent[] {
  try {
    // Format: [channelName, [[id, {data: jsonString}], ...]]
    const [, eventMessages] = JSON.parse(eventData);
    return eventMessages.map((eventMessage: [string, { data: string }]) => {
      return JSON.parse(eventMessage[1].data) as StreamEvent;
    });
  } catch (e) {
    console.error("Failed to parse stream event:", e);
    return [];
  }
}
